import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { saveCRAAssessment } from "../Redux/feasibility";

const Section = styled.div`
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;

// 제목과 저장 표시를 한 줄에 둔다 — 표시가 나타났다 사라질 때 아래 입력들이 밀리지 않도록.
const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid #dee2e6;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const SaveStatus = styled.span`
  font-size: 12px;
  white-space: nowrap;
  color: ${(p) => (p.$saving ? "#6c757d" : "#198754")};
  opacity: ${(p) => (p.$show ? 1 : 0)};
  transition: opacity 0.25s ease;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 13px;
`;

const Label = styled.label`
  color: #495057;
  font-size: 13px;
`;

const Select = styled.select`
  padding: 5px 8px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 13px;
  max-width: 280px;
  background: #fff;
  &:disabled { background: #f8f9fa; color: #6c757d; }
`;

const Textarea = styled.textarea`
  width: 100%;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 7px 10px;
  font-size: 13px;
  resize: vertical;
  min-height: 80px;
  box-sizing: border-box;
  &:disabled { background: #f8f9fa; color: #6c757d; }
`;

const NumInput = styled.input`
  width: 90px;
  padding: 5px 8px;
  border: 1px solid ${(p) => (p.$invalid ? "#dc3545" : "#dee2e6")};
  border-radius: 4px;
  font-size: 13px;
  &:disabled { background: #f8f9fa; color: #6c757d; }
`;

const FieldError = styled.div`
  font-size: 12px;
  color: #dc3545;
  margin-top: 3px;
`;

const ToggleWrap = styled.div`
  display: flex;
  gap: 6px;
`;

const ToggleBtn = styled.button`
  padding: 4px 14px;
  border-radius: 4px;
  border: 1px solid ${(p) => (p.$active ? (p.$yes ? "#198754" : "#dc3545") : "#dee2e6")};
  background: ${(p) => (p.$active ? (p.$yes ? "#198754" : "#dc3545") : "#fff")};
  color: ${(p) => (p.$active ? "#fff" : "#6c757d")};
  font-size: 13px;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SaveBtn = styled.button`
  margin-top: 8px;
  padding: 6px 18px;
  border: none;
  border-radius: 4px;
  background: #0d6efd;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SavedNote = styled.span`
  font-size: 12px;
  color: #198754;
  margin-left: 10px;
`;

const ReadonlyMeta = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-top: 8px;
`;

const DEBOUNCE_MS = 1500;

// CRA가 수동 입력하는 도메인 점수 (가중치는 참고 표기용)
const SCORE_FIELDS = [
  // { key: "scoreEnrollment",  label: "Enrollment Score",  weight: "30%" },
  // { key: "scoreOperational", label: "Operational Score", weight: "25%" },
  // { key: "scoreRegulatory",  label: "Regulatory Score",  weight: "20%" },
  // { key: "scoreQuality",     label: "Quality Score",     weight: "15%" },
  // { key: "scoreFacility",    label: "Facility Score",    weight: "10%" },
  { key: "scoreEnrollment",  label: "Enrollment Score"  },
  { key: "scoreOperational", label: "Operational Score" },
  { key: "scoreRegulatory",  label: "Regulatory Score"  },
  { key: "scoreQuality",     label: "Quality Score"     },
  { key: "scoreFacility",    label: "Facility Score"    },
];

const SCORE_KEYS = [...SCORE_FIELDS.map((d) => d.key), "scoreFinal"];

// 점수 컬럼은 decimal(5,2)이고 점수 자체는 0–100이다. 범위를 벗어난 값은 드라이버가
// 자릿수를 넘겨 보내고(값×100을 정밀도 5로 기술한다), SQL Server는 저장 요청 전체를
// "not a valid instance of data type decimal"로 거부한다 — 한 칸 때문에 나머지 평가까지
// 통째로 날아간다. min/max 속성만으로는 제출을 막지 못하므로 여기서 확인한다.
// 서버도 같은 기준으로 막지만, 어느 칸이 문제인지 짚어줄 수 있는 건 이쪽뿐이다.
const SCORE_MIN = 0;
const SCORE_MAX = 100;

// "" → null(미입력). 그 외에는 숫자로 읽고 저장되는 자리수(소수 둘째 자리)로 맞춘다.
export function parseScore(v) {
  if (v === "" || v == null) return { value: null };
  const n = Number(v);
  if (!Number.isFinite(n)) return { error: "숫자만 입력할 수 있습니다." };
  if (n < SCORE_MIN || n > SCORE_MAX) {
    return { error: `${SCORE_MIN}–${SCORE_MAX} 사이여야 합니다.` };
  }
  return { value: Math.round(n * 100) / 100 };
}

export default function CRAAssessmentPanel({ sfqId, inv, isCRA, onSaved }) {
  const dispatch = useDispatch();

  // ── Initial Assessment state ──────────────────────────────────────────────
  const [init, setInit] = useState({
    piExp:     inv.craInitialPiExp      || "",
    gcpStatus: inv.craInitialGcpStatus  || "",
    regStatus: inv.craInitialRegStatus  || "",
    risk:      inv.craInitialRisk       || "",
    remarks:   inv.craInitialRemarks    || "",
  });
  const initTimer   = useRef(null);
  const pendingInit = useRef(null);
  // "" | "saving" | "saved" — 저장은 화면을 다시 그리지 않으므로, 저장됐다는 사실은
  // 이 한 줄로만 알린다. saved는 잠깐 뒤에 스스로 사라진다.
  const [initStatus, setInitStatus] = useState("");
  const savedTimer  = useRef(null);

  // ── Final Assessment state ────────────────────────────────────────────────
  const [fin, setFin] = useState({
    opinion:          inv.craFinalOpinion        || "",
    basisConfirmed:   inv.craFinalBasisConfirmed || "",
    recommendation:   inv.craFinalRecommendation || "",
    riskLevel:        inv.craFinalRiskLevel      || "",
    scoreEnrollment:  inv.scoreEnrollment  ?? "",
    scoreOperational: inv.scoreOperational ?? "",
    scoreRegulatory:  inv.scoreRegulatory  ?? "",
    scoreQuality:     inv.scoreQuality     ?? "",
    scoreFacility:    inv.scoreFacility    ?? "",
    scoreFinal:       inv.scoreFinal       ?? "",
  });
  const [finSaving, setFinSaving] = useState(false);
  const [finSaved,  setFinSaved]  = useState(false);
  const [scoreErrors, setScoreErrors] = useState({});

  // 점수를 고치면 그 칸의 경고는 즉시 거둔다 — 다시 저장을 눌러야 알 수 있게 두면
  // 무엇을 고쳐야 하는지 알려주는 의미가 없다.
  function updateScore(key, value) {
    setFin((f) => ({ ...f, [key]: value }));
    setFinSaved(false);
    setScoreErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  const initialEditable = isCRA && ["Pending", "Sent", "InProgress", "Submitted"].includes(inv.status);
  // Marking the invitation Reviewed freezes the review pass, so the owner finalizes
  // exactly what was reviewed; correcting it afterwards means rolling back to Submitted
  // with a reason. Mirrors CRA_REVIEW_EDIT_STATUSES in services/Feasibility/index.js,
  // which is what actually enforces it — this only keeps the UI honest about it.
  const finalEditable   = isCRA && ["Submitted"].includes(inv.status);

  // The server refuses a save once the invitation is frozen. That is reachable by racing
  // a debounce against a status change, so report it instead of dropping it silently.
  const runSave = useCallback(async (type, data) => {
    try {
      await dispatch(saveCRAAssessment({ sfqId, invId: inv.id, type, data })).unwrap();
      // 저장한 값은 리듀서가 currentInvitation에 그대로 반영한다. Initial은 서버가 따로
      // 만들어내는 값이 없어 다시 읽을 이유가 없고, 다시 읽으면 상세가 통째로 갈아끼워져
      // 입력 중이던 자리를 잃는다. Final은 craFinalBy/craFinalAt를 서버가 찍으므로
      // 저장을 누른 그때만 새로 읽는다.
      if (type === "final") onSaved?.();
      return true;
    } catch (e) {
      alert(e?.message || "저장에 실패했습니다.");
      return false;
    }
  }, [dispatch, sfqId, inv.id, onSaved]);

  const saveInitial = useCallback(async (data) => {
    clearTimeout(initTimer.current);
    clearTimeout(savedTimer.current);
    initTimer.current   = null;
    pendingInit.current = null;
    setInitStatus("saving");
    const ok = await runSave("initial", data);
    // 저장이 겹쳐 들어오면 먼저 끝난 쪽의 타이머가 나중 표시를 지워버린다 — 매번 갈아끼운다.
    clearTimeout(savedTimer.current);
    setInitStatus(ok ? "saved" : "");
    if (ok) savedTimer.current = setTimeout(() => setInitStatus(""), 2000);
  }, [runSave]);

  // Discrete choices save at once — there is nothing to coalesce, and debouncing them
  // only widened the window in which a save could outlive the panel. Only free text is
  // debounced, and it flushes on blur so leaving never strands a pending edit.
  function updateInit(key, value) {
    if (!initialEditable) return;
    const updated = { ...init, [key]: value };
    setInit(updated);
    if (key !== "remarks") { saveInitial(updated); return; }
    clearTimeout(initTimer.current);
    pendingInit.current = updated;
    initTimer.current   = setTimeout(() => saveInitial(updated), DEBOUNCE_MS);
  }

  function flushInit() {
    if (pendingInit.current) saveInitial(pendingInit.current);
  }

  // Cancel on unmount rather than fire: a timer outliving the panel posts an edit the
  // user can no longer see, possibly after the status has moved on. Blur-flushing above
  // is what makes cancelling safe — switching tabs blurs the textarea first.
  useEffect(() => () => {
    clearTimeout(initTimer.current);
    clearTimeout(savedTimer.current);
  }, []);

  async function saveFinal() {
    // 점수는 숫자(또는 빈값→null)로 정규화 — 표시부의 toFixed() 및 DB Decimal 정합.
    // 한 칸이라도 범위를 벗어나면 보내지 않는다: 서버는 UPDATE 하나로 쓰므로 거절당하면
    // 의견·권고까지 함께 저장되지 않는다.
    const errors = {};
    const scores = {};
    for (const key of SCORE_KEYS) {
      const { value, error } = parseScore(fin[key]);
      if (error) errors[key] = error;
      else scores[key] = value;
    }
    setScoreErrors(errors);
    if (Object.keys(errors).length) return;

    setFinSaving(true);
    setFinSaved(false);
    const ok = await runSave("final", {
      opinion:        fin.opinion,
      basisConfirmed: fin.basisConfirmed,
      recommendation: fin.recommendation,
      riskLevel:      fin.riskLevel,
      ...scores,
    });
    setFinSaved(ok);
    setFinSaving(false);
  }

  return (
    <>
      {/* CRA Initial Assessment */}
      <Section>
        <SectionHead>
          <SectionTitle>Initial Assessment</SectionTitle>
          <SaveStatus $show={!!initStatus} $saving={initStatus === "saving"}>
            {initStatus === "saving" ? "저장 중..." : "✓ 저장됨"}
          </SaveStatus>
        </SectionHead>

        <FieldRow>
          <Label>PI Trial Experience</Label>
          <Select value={init.piExp} disabled={!initialEditable}
            onChange={(e) => updateInit("piExp", e.target.value)}>
            <option value="">Select...</option>
            <option value="None">None</option>
            <option value="1-2">1–2 studies</option>
            <option value="3-5">3–5 studies</option>
            <option value="5+">5+ studies</option>
          </Select>
        </FieldRow>

        <FieldRow>
          <Label>GCP Compliance History</Label>
          <Select value={init.gcpStatus} disabled={!initialEditable}
            onChange={(e) => updateInit("gcpStatus", e.target.value)}>
            <option value="">Select...</option>
            <option value="Good">Good</option>
            <option value="MinorIssues">Minor Issues</option>
            <option value="MajorIssues">Major Issues</option>
            <option value="Unknown">Unknown</option>
          </Select>
        </FieldRow>

        <FieldRow>
          <Label>Regulatory Status</Label>
          <Select value={init.regStatus} disabled={!initialEditable}
            onChange={(e) => updateInit("regStatus", e.target.value)}>
            <option value="">Select...</option>
            <option value="Current">Current</option>
            <option value="Pending">Pending</option>
            <option value="Issues">Issues</option>
            <option value="Unknown">Unknown</option>
          </Select>
        </FieldRow>

        <FieldRow>
          <Label>Preliminary Risk</Label>
          <Select value={init.risk} disabled={!initialEditable}
            onChange={(e) => updateInit("risk", e.target.value)}>
            <option value="">Select...</option>
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
            <option value="Critical">Critical</option>
          </Select>
        </FieldRow>

        <FieldRow style={{ alignItems: "flex-start" }}>
          <Label style={{ paddingTop: 6 }}>Initial Remarks</Label>
          <Textarea
            value={init.remarks}
            disabled={!initialEditable}
            placeholder="Initial observations, concerns, or notes..."
            onChange={(e) => updateInit("remarks", e.target.value)}
            onBlur={flushInit}
          />
        </FieldRow>

        {isCRA && !initialEditable && (
          <ReadonlyMeta>
            Reviewed 이후에는 수정할 수 없습니다 — 수정하려면 상태를 되돌려 주세요.
          </ReadonlyMeta>
        )}
      </Section>

      {/* CRA Final Assessment */}
      <Section>
        <SectionHead>
          <SectionTitle>Final Assessment</SectionTitle>
        </SectionHead>

        <FieldRow style={{ alignItems: "flex-start" }}>
          <Label style={{ paddingTop: 6 }}>Review Opinion</Label>
          <Textarea
            value={fin.opinion}
            disabled={!finalEditable}
            placeholder="Overall review opinion and findings..."
            onChange={(e) => setFin((f) => ({ ...f, opinion: e.target.value }))}
          />
        </FieldRow>

        <FieldRow>
          <Label>Basis Confirmed</Label>
          <ToggleWrap>
            <ToggleBtn $yes $active={fin.basisConfirmed === "Y"} disabled={!finalEditable}
              onClick={() => setFin((f) => ({ ...f, basisConfirmed: "Y" }))}>
              Yes
            </ToggleBtn>
            <ToggleBtn $active={fin.basisConfirmed === "N"} disabled={!finalEditable}
              onClick={() => setFin((f) => ({ ...f, basisConfirmed: "N" }))}>
              No
            </ToggleBtn>
          </ToggleWrap>
        </FieldRow>

        <FieldRow>
          <Label>Final Recommendation</Label>
          <Select value={fin.recommendation} disabled={!finalEditable}
            onChange={(e) => setFin((f) => ({ ...f, recommendation: e.target.value }))}>
            <option value="">Select...</option>
            <option value="Recommended">Recommended</option>
            <option value="NotRecommended">Not Recommended</option>
            <option value="Conditional">Conditional</option>
          </Select>
        </FieldRow>

        {[...SCORE_FIELDS, { key: "scoreFinal", label: "Final Score" }].map((d) => (
          <FieldRow key={d.key} style={scoreErrors[d.key] ? { alignItems: "flex-start" } : undefined}>
            {/* <Label>{d.label} <span style={{ color: "#adb5bd", fontSize: 11 }}>({d.weight})</span></Label> */}
            <Label style={scoreErrors[d.key] ? { paddingTop: 6 } : undefined}>{d.label}</Label>
            <div>
              <NumInput type="number" min={SCORE_MIN} max={SCORE_MAX} step="0.01"
                value={fin[d.key]} disabled={!finalEditable}
                $invalid={!!scoreErrors[d.key]}
                aria-invalid={!!scoreErrors[d.key]}
                onChange={(e) => updateScore(d.key, e.target.value)} />
              {scoreErrors[d.key] && <FieldError>{scoreErrors[d.key]}</FieldError>}
            </div>
          </FieldRow>
        ))}

        <FieldRow>
          <Label>Final Risk Level</Label>
          <Select value={fin.riskLevel} disabled={!finalEditable}
            onChange={(e) => setFin((f) => ({ ...f, riskLevel: e.target.value }))}>
            <option value="">Select...</option>
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
            <option value="Critical">Critical</option>
          </Select>
        </FieldRow>

        {finalEditable && (
          <div>
            <SaveBtn onClick={saveFinal} disabled={finSaving}>
              {finSaving ? "Saving..." : "Save Final Assessment"}
            </SaveBtn>
            {finSaved && <SavedNote>Saved</SavedNote>}
          </div>
        )}

        {isCRA && !finalEditable && (
          <ReadonlyMeta>
            {["Reviewed", "Finalized"].includes(inv.status)
              ? "Reviewed 이후에는 수정할 수 없습니다 — 수정하려면 상태를 되돌려 주세요."
              : "사이트가 제출(Submitted)한 뒤에 작성할 수 있습니다."}
          </ReadonlyMeta>
        )}

        {inv.craFinalBy && (
          <ReadonlyMeta>
            Completed by: {inv.craFinalBy}
            {inv.craFinalAt && ` · ${new Date(inv.craFinalAt).toLocaleString("ko-KR")}`}
          </ReadonlyMeta>
        )}
      </Section>
    </>
  );
}
